import { useEffect, useState } from 'react';
import {
  Card,
  Row,
  Col,
  Button,
  Empty,
  message,
  Typography,
  Space,
  Tag,
  Popconfirm,
} from 'antd';
import { PlusOutlined, ExperimentOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import request from '@/utils/request';
import { formatDate } from '@/utils/format';
import { thumb } from '@/utils/image';

interface ExperimentRow {
  id: number;
  name: string;
  status: 'active' | 'archived';
  coverImageId: number | null;
  recordsCount: number;
  createdAt: string;
  updatedAt: string;
}

const ExperimentListPage: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<ExperimentRow[]>([]);
  const [urlMap, setUrlMap] = useState<Map<number, string>>(new Map());
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await request.get<{ items: ExperimentRow[] }>('/creator/experiments');
      const data = (res.data as unknown as { items: ExperimentRow[] }).items;
      setItems(data);
      const needIds = data.map((d) => d.coverImageId).filter((v): v is number => v != null);
      if (needIds.length > 0) {
        const imgRes = await request.get<{ items: { id: number; accessUrl: string }[] }>(
          `/creator/images?ids=${needIds.join(',')}`,
        );
        const map = new Map<number, string>();
        for (const im of (imgRes.data as unknown as { items: { id: number; accessUrl: string }[] }).items) {
          map.set(im.id, im.accessUrl);
        }
        setUrlMap(map);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onCreate = async () => {
    if (creating) return;
    setCreating(true);
    try {
      // 不传 name: 服务端默认 "未命名实验 N", 创作者进入工作台后可直接改名.
      const res = await request.post<ExperimentRow>('/creator/experiments', {});
      const data = res.data as unknown as ExperimentRow;
      navigate(`/experiments/${data.id}`);
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    } finally {
      setCreating(false);
    }
  };

  const onDelete = async (id: number) => {
    try {
      await request.delete(`/creator/experiments/${id}`);
      // Optimistic remove + drop any cached draft so a future experiment
      // with the same id (highly unlikely but safe) starts clean.
      setItems((prev) => prev.filter((it) => it.id !== id));
      try {
        window.localStorage.removeItem(`experiment-draft-v2-${id}`);
      } catch {
        /* noop */
      }
      message.success('已删除');
    } catch (e) {
      message.error(e instanceof Error ? e.message : '删除失败');
    }
  };

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Space style={{ justifyContent: 'space-between', width: '100%' }}>
        <div>
          <Typography.Title level={3} style={{ margin: 0 }}>
            创作实验
          </Typography.Title>
          <Typography.Text type="secondary">
            每个实验台聚焦一个方向，上传素材、勾选模型、批量对照效果
          </Typography.Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} loading={creating} onClick={onCreate}>
          新建实验
        </Button>
      </Space>

      {items.length === 0 && !loading ? (
        <Card>
          <Empty className="brand-empty" description="还没有实验，点击右上角新建开始创作">
            <Button type="primary" loading={creating} onClick={onCreate} icon={<PlusOutlined />}>
              新建实验
            </Button>
          </Empty>
        </Card>
      ) : (
        <Row gutter={[16, 16]}>
          {items.map((item) => {
            const coverUrl = item.coverImageId ? urlMap.get(item.coverImageId) : null;
            return (
              <Col key={item.id} xs={24} sm={12} md={8} lg={6}>
                <Card
                  hoverable
                  loading={loading}
                  onClick={() => navigate(`/experiments/${item.id}`)}
                  style={{ overflow: 'hidden', position: 'relative' }}
                  cover={
                    <div
                      style={{
                        height: 150,
                        background: coverUrl
                          ? undefined
                          : 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A40 60%, #F6F3EC 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#B45309',
                        overflow: 'hidden',
                      }}
                    >
                      {coverUrl ? (
                        <img
                          src={thumb(coverUrl, 180)}
                          alt={item.name}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      ) : (
                        <ExperimentOutlined style={{ fontSize: 38 }} />
                      )}
                    </div>
                  }
                >
                  <Card.Meta
                    title={item.name}
                    description={
                      <Space direction="vertical" size={4}>
                        <Tag color="gold" style={{ margin: 0 }}>
                          {item.recordsCount} 条记录
                        </Tag>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          更新于 {formatDate(item.updatedAt)}
                        </Typography.Text>
                      </Space>
                    }
                  />
                  <Popconfirm
                    title="删除该实验？"
                    description={
                      item.recordsCount > 0
                        ? `将一并清除 ${item.recordsCount} 条实验记录，不可恢复`
                        : '不可恢复'
                    }
                    okText="删除"
                    okButtonProps={{ danger: true }}
                    cancelText="取消"
                    onConfirm={(e) => {
                      e?.stopPropagation();
                      onDelete(item.id);
                    }}
                    onCancel={(e) => e?.stopPropagation()}
                  >
                    <Button
                      size="small"
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        background: 'rgba(255,255,255,0.85)',
                        backdropFilter: 'blur(4px)',
                      }}
                    />
                  </Popconfirm>
                </Card>
              </Col>
            );
          })}
        </Row>
      )}

    </Space>
  );
};

export default ExperimentListPage;
